import React, { MouseEvent, useContext, useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import queryString from 'query-string'
import values from 'lodash/values'
import { useLocation } from 'react-router-dom'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { DndProvider } from 'react-dnd'
import Grid from '@mui/material/Grid'

import { Paragraph, Popper, Title } from 'components/shared'
import { DroppableAreaCol, TaskFilter, TaskModal } from 'components/Admin'
import { DoneButton, EmptyBlock, TaskList, Wrapper } from './Admin.style'

import { history } from 'store/configureStore'
import { IPostClientTask, ITask, ITaskType, StatusTypeIds } from 'store/taskManager/model'
import {
  changeActiveTaskAction,
  moveTaskAction,
  postTaskAction,
  startWatchTasksAction,
  stopWatchTasksAction,
} from 'store/taskManager/actions'
import { BLOCK_SCROLL_TOP_STATE } from '../../components/Common/ScrollToTop/consts'
import { useTitle } from 'hooks/useTitle'
import { AppContext, Text, useHasUserTag, userEffectorModel } from '@centra/centra-ui'
import { CreateTaskModalRemote } from 'components/CreateTaskModalRemote'
import { IClientTaskFormData } from 'tasktracker/_types/models/task'
import { useUnit } from 'effector-react'

interface IQueryParams {
  accountManagerIds?: number[]
  advertiserIds?: number[]
  agencyIds?: number[]
  statusIds?: number[]
  categoryIds?: number[]
  task?: number
}

const AdminDashboard = () => {
  const { search } = useLocation()
  const { setNotification } = useContext(AppContext)
  const dispatch = useDispatch()
  const {
    tasks: { urgent, managers, clients, done },
  } = useSelector(
    ({ taskManager }) => ({
      tasks: taskManager.tasks,
      taskManagerFilter: taskManager.filter,
    }),
    shallowEqual
  )
  const [taskModalOpen, setTaskModalOpen] = useState(false)
  const [isTaskModalCreating, setIsTaskModalCreating] = useState(false)
  const [isOpenCreateTask, setIsOpenCreateTask] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const { $userStore: user } = useUnit(userEffectorModel)
  const [visibleDoneCol, setVisibleDoneCol] = useState(true)
  const [anchorPopover, setAnchorPopover] = useState<HTMLElement | null>(null)
  const isAccountManager = useHasUserTag(['role.account-manager'])

  const onSubmitTask = (data: IClientTaskFormData) => {
    setSubmitting(true)
    if (user?.id) {
      dispatch(
        postTaskAction(
          {
            ...(data as IPostClientTask),
            client: {
              name: '',
              id: user?.id,
            },
            location: 'advertiser_account',
          },
          () => {
            setSubmitting(false)
            setIsOpenCreateTask(false)
          }
        )
      )
    }
  }

  const memoFilters = useMemo(() => {
    const params = queryString.parse(search, { arrayFormat: 'bracket', parseNumbers: true })
    // eslint-disable-next-line
    const { task, ...filters }: IQueryParams = params
    return filters
  }, [search])

  const isAppliedFilters = useMemo(() => {
    return !!values(memoFilters).length
  }, [memoFilters])

  useEffect(() => {
    const params = queryString.parse(search, { arrayFormat: 'bracket', parseNumbers: true })
    const { task }: IQueryParams = params
    dispatch(startWatchTasksAction())

    if (task && Number.isInteger(task)) {
      onActivate(task)
    }

    return () => {
      dispatch(stopWatchTasksAction())
    }
  }, [])

  const onActivate = (id: number, e?: MouseEvent<any>) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()

      if (e.metaKey) {
        window.open(`/?task=${id}`)
        return false
      }
    }
    setTaskModalOpen(true)
    setIsTaskModalCreating(!id)

    dispatch(
      changeActiveTaskAction({ id }, ({ success }) => {
        if (success) {
          const urlFilters = memoFilters && queryString.stringify(memoFilters, { arrayFormat: 'bracket' })
          if (urlFilters) {
            history.replace(`/?${queryString.stringify(memoFilters, { arrayFormat: 'bracket' })}&task=${id}`)
          } else {
            history.replace(`/?task=${id}`)
          }
        }
      })
    )
  }

  const onCloseTask = () => {
    history.replace(
      `/${memoFilters && '?' + queryString.stringify(memoFilters, { arrayFormat: 'bracket' })}`,
      BLOCK_SCROLL_TOP_STATE
    )
    setTaskModalOpen(false)
  }

  const showDoneButtonTooltip = (event: MouseEvent<HTMLElement>) => {
    setAnchorPopover(event.currentTarget)
  }

  const toggleDoneCol = () => {
    setAnchorPopover(null)
    setVisibleDoneCol(!visibleDoneCol)
  }

  const onMoveTask = (taskType: ITaskType, task: ITask) => {
    if (taskType !== task.taskType) {
      dispatch(moveTaskAction({ taskType, task }))
    }
  }

  const onMoveClientTask = (taskType: ITaskType, task: ITask) => {
    if (task.status.id === StatusTypeIds.onConfirmed && !task.isClientConfirm) {
      setNotification({
        view: 'error',
        title: 'Задача не согласована клиентом',
        content: 'Перевести задачу в статус Готово можно только после согласования клиентом в тикете',
      })

      return
    }

    onMoveTask(taskType, task)
  }

  useTitle('taskList')

  return (
    <Wrapper>
      <Title level={2} style={{ marginBottom: 16 }}>
        Задачи
      </Title>

      <TaskFilter />

      <DndProvider backend={HTML5Backend}>
        <TaskList>
          <Grid container spacing={3} alignItems="stretch" wrap={'nowrap'} style={{ minHeight: 500 }}>
            <DroppableAreaCol
              title={isAccountManager ? 'Мои срочные задачи' : 'Срочные задачи'}
              onActivateTask={onActivate}
              emptyState={
                <EmptyBlock>
                  {isAppliedFilters ? (
                    <Paragraph>Нет задач, соответствующих вашим критериям</Paragraph>
                  ) : (
                    <>
                      <svg width="66px" height="55px">
                        <use xlinkHref="#done" />
                      </svg>
                      {isAccountManager ? (
                        <>
                          <Paragraph>У вас нет срочных задач.</Paragraph>
                          <Paragraph>Чтоб добавить задачу в Срочные, перетащите ее из колонки Мои задачи</Paragraph>
                        </>
                      ) : (
                        <Paragraph>Нет срочных задач</Paragraph>
                      )}
                    </>
                  )}
                </EmptyBlock>
              }
              urgent
              taskList={urgent.items}
              totalCount={urgent.totalCount}
              onDrop={onMoveTask}
              colStatus={'urgent'}
            />
            <DroppableAreaCol
              title={isAccountManager ? 'Мои задачи' : 'Задачи менеджеров'}
              onActivateTask={onActivate}
              emptyState={
                <EmptyBlock>
                  {isAppliedFilters ? (
                    <Paragraph>Нет задач, соответствующих вашим критериям</Paragraph>
                  ) : (
                    <>
                      <svg width="66px" height="55px">
                        <use xlinkHref="#done" />
                      </svg>
                      {isAccountManager ? (
                        <>
                          <Paragraph>У вас нет задач к выполнению</Paragraph>
                          <Text
                            id="createNewTaskText"
                            onClick={() => setIsOpenCreateTask(true)}
                            textDecoration="underline"
                            style={{ cursor: 'pointer' }}
                          >
                            Создать задачу
                          </Text>
                        </>
                      ) : (
                        <Paragraph>Нет задач к выполнению</Paragraph>
                      )}
                    </>
                  )}
                </EmptyBlock>
              }
              managers
              taskList={managers.items}
              totalCount={managers.totalCount}
              onDrop={onMoveTask}
              colStatus={'managers'}
            />
            <DroppableAreaCol
              onActivateTask={onActivate}
              title={'Задачи клиентов'}
              taskList={clients.items}
              totalCount={clients.totalCount}
              onDrop={onMoveTask}
              colStatus={'clients'}
              emptyState={
                isAppliedFilters && (
                  <EmptyBlock>
                    <Paragraph>Нет задач, соответствующих вашим критериям</Paragraph>
                  </EmptyBlock>
                )
              }
            />

            {visibleDoneCol && (
              <DroppableAreaCol
                onActivateTask={onActivate}
                title={'Выполненные'}
                taskList={done.items}
                totalCount={done.totalCount}
                onDrop={onMoveClientTask}
                colStatus={'done'}
                emptyState={
                  isAppliedFilters && (
                    <EmptyBlock>
                      <Paragraph>Нет задач, соответствующих вашим критериям</Paragraph>
                    </EmptyBlock>
                  )
                }
              />
            )}
          </Grid>

          <DoneButton
            $shape={'circle'}
            size={'sm'}
            themetype={'grey'}
            onClick={toggleDoneCol}
            onMouseEnter={showDoneButtonTooltip}
            onMouseLeave={() => setAnchorPopover(null)}
          >
            {visibleDoneCol ? (
              <svg width="16px" height="16px">
                <use xlinkHref="#minus" />
              </svg>
            ) : (
              <svg width="16px" height="16px">
                <use xlinkHref="#plus" />
              </svg>
            )}
          </DoneButton>
        </TaskList>
      </DndProvider>

      <Popper anchorEl={anchorPopover} placement={'bottom-end'} width={104} textAlign={'center'}>
        {visibleDoneCol ? (
          <Paragraph size={'sm'}>Скрыть выполненные</Paragraph>
        ) : (
          <Paragraph size={'sm'}>Показать выполненные</Paragraph>
        )}
      </Popper>

      {taskModalOpen && (
        <TaskModal open={taskModalOpen} onClose={onCloseTask} isTaskModalCreating={isTaskModalCreating} />
      )}

      <CreateTaskModalRemote
        isOpen={isOpenCreateTask}
        onClose={() => setIsOpenCreateTask(false)}
        onSubmit={onSubmitTask}
        submitting={submitting}
      />
    </Wrapper>
  )
}

export default AdminDashboard
